import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { Observable } from 'rxjs';
import { EMPLOYEE_STATUSES, FORMLY_FIELD_TYPES, FORMLY_FIELD_VALIDATORS } from '@app/constants';
import { EmployeeModel } from '@app/modules/fund-management/reports/models/employee.model';
import { ApiResponse } from '@app/shared/models/common';
import { isNil } from '@app/utils';
import { CsvUidModel } from '@app/utils/csvReader';

@Injectable()
export class EmployeeService {
	endpointBase = '/api/employee';

	constructor(private readonly http: HttpClient) {}

	getForDropdowns() {
		return this.http.get(`${this.endpointBase}/for-dropdowns`);
	}

	getEmployee(id: number): Observable<any> {
		return this.http.get(`${this.endpointBase}`, { params: { id } });
	}

	getLicensureForEmployee(id: number): Observable<any> {
		return this.http.get(`${this.endpointBase}/${id}/licensure`);
	}

	getEmployeeDemographics(id: number): Observable<any> {
		return this.http.get(`${this.endpointBase}/getManageEmployeeDemographics`, { params: { id: id.toString() } });
	}

	getEmployeeJobs(id: number): Observable<any> {
		return this.http.get(`${this.endpointBase}/getManageEmployeeJobs`, { params: { id: id.toString() } });
	}

	getEmployeeTax(id: number): Observable<any> {
		return this.http.get(`/api/employeetax/editor/${id}`);
	}

	getEmployeeEligibleDeductions(id: number): Observable<any> {
		return this.http.get(`${this.endpointBase}/getEmployeeEligibleDeductions`, { params: { employeeId: id.toString() } });
	}

	getDatesOfService(id: number): Observable<any> {
		return this.http.get(`${this.endpointBase}/getDatesOfService`, { params: { employeeId: id.toString() } });
	}

	getServiceTypes(): Observable<any> {
		return this.http.get('/api/employeeservicetype/list');
	}

	updateStaffUID(uidImport: CsvUidModel[], ignoreUnitCheck: boolean): Observable<any> {
		return this.http.post(`${this.endpointBase}/updateStaffUID`, uidImport, { params: { ignoreUnitCheck: ignoreUnitCheck.toString() } });
	}

	getEmployeeDemographicsFormFields(): FormlyFieldConfig[] {
		return [
			{
				fieldGroupClassName: 'row',
				fieldGroup: [
					{
						key: 'EmployeeNumber',
						type: FORMLY_FIELD_TYPES.input,
						className: 'col-xs-6',
						templateOptions: {
							label: 'Employee #',
							readonly: true,
						},
					},
					{
						key: 'StatusId',
						type: FORMLY_FIELD_TYPES.select,
						className: 'col-xs-6',
						templateOptions: {
							label: 'Status',
							required: true,
						},
						expressionProperties: {
							'templateOptions.options': 'formState.selects.demographics.statusOptions',
						},
					},
				],
			},
			{
				fieldGroupClassName: 'row',
				fieldGroup: [
					{
						className: 'col-md-7 col-xs-12',
						fieldGroupClassName: 'row',
						fieldGroup: [
							{
								key: 'FirstName',
								type: FORMLY_FIELD_TYPES.input,
								className: 'col-md-3 col-xs-6',
								templateOptions: {
									label: 'First Name',
									required: true,
									maxLength: 30,
								},
								validators: {
									validation: [FORMLY_FIELD_VALIDATORS.requiredNoWhitespace],
								},
							},
							{
								key: 'MiddleName',
								type: FORMLY_FIELD_TYPES.input,
								className: 'col-md-3 col-xs-6',
								templateOptions: {
									label: 'Middle Name',
									maxLength: 30,
								},
							},
							{
								key: 'LastName',
								type: FORMLY_FIELD_TYPES.input,
								className: 'col-md-3 col-xs-6',
								templateOptions: {
									label: 'Last Name',
									required: true,
									maxLength: 30,
								},
								validators: {
									validation: [FORMLY_FIELD_VALIDATORS.requiredNoWhitespace],
								},
							},
							{
								key: 'Suffix',
								type: FORMLY_FIELD_TYPES.select,
								className: 'col-md-3 col-xs-6',
								templateOptions: {
									label: 'Suffix',
								},
								expressionProperties: {
									'templateOptions.options': 'formState.selects.demographics.suffixOptions',
								},
							},
							{
								key: 'PreviousName',
								type: FORMLY_FIELD_TYPES.input,
								className: 'col-xs-6',
								templateOptions: {
									label: 'Previous Name',
									maxLength: 104,
								},
							},
							{
								key: 'PreferredName',
								type: FORMLY_FIELD_TYPES.input,
								className: 'col-xs-6',
								templateOptions: {
									label: 'Preferred Name',
									maxLength: 50,
								},
							},
							{
								key: 'SSN',
								type: FORMLY_FIELD_TYPES.maskedInput,
								className: 'col-md-4 col-xs-6',
								templateOptions: {
									label: 'SSN',
									maxLength: 11,
									maskString: '000-00-0000',
									required: true,
								},
								validators: {
									validation: [FORMLY_FIELD_VALIDATORS.requiredNoWhitespace],
								},
							},
							{
								key: 'Sex',
								type: FORMLY_FIELD_TYPES.select,
								className: 'col-md-4 col-xs-6',
								templateOptions: {
									label: 'Gender',
									required: true,
								},
								expressionProperties: {
									'templateOptions.options': 'formState.selects.genderOptions',
								},
							},
							{
								key: 'BirthDT',
								type: FORMLY_FIELD_TYPES.datepicker,
								className: 'col-md-4 col-xs-6',
								templateOptions: {
									label: 'Birth Date',
									required: true,
								},
							},
							{
								key: 'PrimarySiteId',
								type: FORMLY_FIELD_TYPES.select,
								className: 'col-md-8 col-xs-6',
								templateOptions: {
									label: 'Primary Site',
									required: true,
								},
								expressionProperties: {
									'templateOptions.options': 'formState.selects.siteOptions',
								},
							},
							{
								key: 'StaffNumber',
								type: FORMLY_FIELD_TYPES.input,
								className: 'col-md-4 col-xs-6',
								templateOptions: {
									label: 'State ID',
									readonly: true,
								},
							},
							{
								key: 'Country',
								type: FORMLY_FIELD_TYPES.select,
								className: 'col-xs-12',
								templateOptions: {
									label: 'Country',
									required: true,
									valueProp: 'CountryCode',
									labelProp: 'CountryDisplay',
								},
								expressionProperties: {
									'templateOptions.options': 'formState.selects.countryOptions',
								},
							},
							{
								key: 'Address1',
								type: FORMLY_FIELD_TYPES.input,
								className: 'col-xs-6',
								templateOptions: {
									label: 'Address 1',
									required: true,
									maxLength: 50,
								},
								validators: {
									validation: [FORMLY_FIELD_VALIDATORS.requiredNoWhitespace],
								},
							},
							{
								key: 'Address2',
								type: FORMLY_FIELD_TYPES.input,
								className: 'col-xs-6',
								templateOptions: {
									label: 'Address 2',
									maxLength: 50,
								},
							},
							{
								key: 'City',
								type: FORMLY_FIELD_TYPES.input,
								className: 'col-xs-6',
								templateOptions: {
									label: 'City',
									required: true,
									maxLength: 50,
								},
								validators: {
									validation: [FORMLY_FIELD_VALIDATORS.requiredNoWhitespace],
								},
							},
							{
								key: 'State',
								type: FORMLY_FIELD_TYPES.select,
								className: 'col-md-4 col-xs-6',
								templateOptions: {
									label: 'State',
									required: true,
									options: [],
									valueProp: 'StateCode',
									labelProp: 'StateDisplay',
								},
								expressionProperties: {
									'templateOptions.options': (model: any, formState: any) =>
										formState.selects.countryOptions.find((item) => item.CountryCode === model.Country)?.StateCodes || [],
									// reset model when updating select options
									'model.State': `field.templateOptions.options.some(item => item.StateCode === model.State) ? model.State : null`,
								},
								hideExpression: 'field.templateOptions.options.length <= 1',
							},
							{
								key: 'Zip',
								type: FORMLY_FIELD_TYPES.input,
								className: 'col-md-2 col-xs-6',
								templateOptions: {
									label: 'ZIP',
									required: true,
									maxLength: 10,
								},
								validators: {
									validation: [FORMLY_FIELD_VALIDATORS.requiredNoWhitespace],
								},
							},
							{
								key: 'Email',
								type: FORMLY_FIELD_TYPES.input,
								className: 'col-xs-6',
								templateOptions: {
									type: 'email',
									label: 'Email',
									maxLength: 50,
								},
							},
							{
								key: 'Phone',
								type: FORMLY_FIELD_TYPES.maskedInput,
								className: 'col-xs-6',
								templateOptions: {
									label: 'Phone Number',
									maxLength: 50,
									maskString: '000-000-0000',
								},
							},
							{
								key: 'HighestDegree',
								type: FORMLY_FIELD_TYPES.select,
								className: 'col-xs-6',
								templateOptions: {
									label: 'Highest Degree Earned',
								},
								expressionProperties: {
									'templateOptions.options': 'formState.selects.demographics.highestDegreeOptions',
								},
							},
							{
								key: 'MaritalStatusId',
								type: FORMLY_FIELD_TYPES.select,
								className: 'col-xs-6',
								templateOptions: {
									label: 'Marital Status',
								},
								expressionProperties: {
									'templateOptions.options': 'formState.selects.demographics.maritalStatusOptions',
								},
							},
						],
					},
					{
						className: 'col-md-offset-1 col-md-4 col-xs-12',
						fieldGroupClassName: 'row',
						fieldGroup: [
							{
								key: 'HireDT',
								type: FORMLY_FIELD_TYPES.datepicker,
								className: 'col-xs-6',
								templateOptions: {
									label: 'Hire Date',
									required: true,
								},
							},
							{
								key: 'PreviousHireDT',
								type: FORMLY_FIELD_TYPES.datepicker,
								className: 'col-xs-6',
								templateOptions: {
									label: 'Previous Hire Date',
									required: true,
								},
							},
							{
								key: 'StateHireDT',
								type: FORMLY_FIELD_TYPES.datepicker,
								className: 'col-xs-6',
								templateOptions: {
									label: 'State Hire Date',
									required: true,
								},
							},
							{
								key: 'LongevityMonth',
								type: FORMLY_FIELD_TYPES.select,
								className: 'col-xs-6',
								templateOptions: {
									label: 'Longevity Month',
								},
								expressionProperties: {
									'templateOptions.options': 'formState.selects.demographics.longevityMonthOptions',
								},
							},
							{
								key: 'TenureId',
								type: FORMLY_FIELD_TYPES.select,
								className: 'col-xs-6',
								templateOptions: {
									label: 'Tenure Status',
								},
								expressionProperties: {
									'templateOptions.options': 'formState.selects.demographics.tenureStatusOptions',
								},
							},
							{
								key: 'RetirementDT',
								type: FORMLY_FIELD_TYPES.datepicker,
								className: 'col-xs-6',
								templateOptions: {
									label: 'Retirement Date',
									required: true,
								},
								hideExpression: (model: any): boolean => parseFloat(model.StatusId) !== EMPLOYEE_STATUSES.Retired,
							},
							{
								className: 'col-xs-12',
								fieldGroupClassName: 'row',
								hideExpression: (model: any): boolean => parseFloat(model.StatusId) !== EMPLOYEE_STATUSES.Terminated,
								fieldGroup: [
									{
										key: 'TerminationDT',
										type: FORMLY_FIELD_TYPES.datepicker,
										className: 'col-xs-6',
										templateOptions: {
											label: 'Termination Date',
											required: true,
										},
									},
									{
										key: 'TerminationReasonId',
										type: FORMLY_FIELD_TYPES.select,
										className: 'col-xs-6',
										templateOptions: {
											label: 'Termination Reason',
											required: true,
										},
										expressionProperties: {
											'templateOptions.options': 'formState.selects.demographics.terminationReasonOptions',
										},
									},
								],
							},
						],
					},
				],
			},
		];
	}

	searchEmployees(searchText: string, payGroupId?: number, limit?: number): Observable<any> {
		return this.http.get<any>(`${this.endpointBase}/search-employees`, {
			params: { searchText, payGroupId: !isNil(payGroupId) ? payGroupId.toString() : null, limit: !isNil(limit) ? limit.toString() : null },
		});
	}

	getEmployeeList() {
		return this.http.get<ApiResponse<'employees', EmployeeModel[]>>(`${this.endpointBase}/list`);
	}

	adjustEmployeeLeaveBalances(entity: any): Observable<any> {
		return this.http.put<any>(`${this.endpointBase}/adjust-employee-leave-balances`, entity);
	}
}
